import axios from 'axios';
import { secureStorage } from 'services/secure-storage.service';
import { getBackendUrl } from '../config';

// Crea una instancia de Axios
const api = axios.create({
  baseURL: getBackendUrl()
});

// Función para obtener el token de acceso
const getAccessToken = (): string | null => {
  const userStr = secureStorage.getItem('user');
  let user = null;
  if (userStr) user = JSON.parse(userStr);

  if (user && user.accessToken) {
    return user.accessToken; // for Node.js Express back-end
  } else {
    return null;
  }
};

// Función para obtener el token de refresco
const getRefreshToken = (): string | null => {
  const userStr = secureStorage.getItem('user');
  let user = null;
  if (userStr) user = JSON.parse(userStr);

  if (user && user.refreshToken) {
    return user.refreshToken; // for Node.js Express back-end
  } else {
    return null;
  }
};

// Función para almacenar los tokens
const storeTokens = (accessToken: string, refreshToken: string) => {
  const userStr = secureStorage.getItem('user');

  if (userStr) {
    const user = JSON.parse(userStr);
    user.accessToken = accessToken;
    user.refreshToken = refreshToken;
    secureStorage.setItem('user', JSON.stringify(user));
  } else {
    // Manejo del caso en que el objeto user no exista en el secureStorage
    const user = { accessToken, refreshToken };
    secureStorage.setItem('user', JSON.stringify(user));
  }
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token available');

  try {
    const response = await api.post(
      'auth/refreshtoken', // Sin la URL base (ya está en api)
      { refreshToken },
      {
        // Marca esta petición como especial para evitar loops
        headers: {
          'Content-Type': 'application/json',
          // Header que el antivirus reconozca como legítimo
          'X-Requested-With': 'XMLHttpRequest',
          'X-Skip-Auth-Refresh': 'true'
        }
      }
    );

    const { accessToken, refreshToken: newRefreshToken } = response.data;
    storeTokens(accessToken, newRefreshToken);
    return accessToken;
  } catch (error) {
    console.error('Refresh token request failed:', error);
    throw error;
  }
};

// Interceptor para añadir el token a cada solicitud
api.interceptors.request.use(
  config => {
    const token = getAccessToken();
    if (token && config.headers) {
      // Añadir el token a la cabecera 'x-access-token' (o 'Authorization')
      config.headers['x-access-token'] = token;
      //config.headers.Authorization = `Bearer ${token}`; // O Authorization: `Bearer ${token}`
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // Verifica si es una petición de refresh token
    if (originalRequest.headers?.['X-Skip-Auth-Refresh'] === 'true') {
      return Promise.reject(error);
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers['x-access-token'] = newAccessToken;
        return api(originalRequest);
      } catch (e) {
        console.error('Token refresh failed:', e);
        //El refreshtoken ha expirado
        window.location.href = '/errors/403';
        //window.location.href = '/authentication/sign-out'
      }
    } else if (error.response.status === 403) {
      console.error('Acceso denegado: ', error.response.data);
      // Manejo de error 403 específico, mostrar un mensaje
      window.location.href = '/errors/403';
    }
    return Promise.reject(error);
  }
);

export default api;
